export type SchemaNode = Record<string, unknown>

export type Values = Record<string, unknown>

export const isRecord = (value: unknown): value is SchemaNode => typeof value === 'object' && value !== null && !Array.isArray(value)

export const presentString = (value: unknown): string | null => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : null)

export const titleCase = (name: string): string =>
  name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const isBlankValue = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0)

const withoutBlanksIn = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(withoutBlanksIn)
  }
  return isRecord(value) ? withoutBlanks(value) : value
}

export const withoutBlanks = (values: Values): Values =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([, value]) => !isBlankValue(value))
      .map(([name, value]) => [name, withoutBlanksIn(value)]),
  )

// A top-level answer the prefill may still fill: blank, or an object the user left empty.
export const isUnanswered = (entered: unknown): boolean => isBlankValue(entered) || (isRecord(entered) && Object.keys(entered).length === 0)

export const mergePrefilled = (existing: Values, filled: Values | undefined): Values => {
  if (!filled || Object.keys(filled).length === 0) {
    return existing
  }
  const answers = Object.entries(filled).filter(([name]) => isUnanswered(existing[name]))
  return answers.length === 0 ? existing : { ...existing, ...Object.fromEntries(answers) }
}

interface SchemaSlice {
  include?: string[]
  exclude?: string[]
}

export const schemaProperties = (schema: SchemaNode): SchemaNode => (isRecord(schema.properties) ? schema.properties : {})

export const hasProperties = (schema: SchemaNode): boolean => Object.keys(schemaProperties(schema)).length > 0

const rowSchema = (schema: SchemaNode | null): SchemaNode | null => {
  const items: unknown = schema?.items
  return isRecord(items) ? items : null
}

const answeredIn = (schema: SchemaNode | null, value: unknown): unknown => {
  if (Array.isArray(value)) {
    const row = rowSchema(schema)
    return (row ? value.filter((entry) => !isBlankValue(entry)) : value).map((entry) => answeredIn(row, entry))
  }
  return isRecord(value) ? withoutBlanksFor(schema, value) : value
}

export const withoutBlanksFor = (schema: SchemaNode | null, values: Values): Values => {
  const properties = schema ? schemaProperties(schema) : {}
  return Object.fromEntries(
    Object.entries(values)
      .map(([name, value]) => {
        const child: unknown = properties[name]
        return [name, answeredIn(isRecord(child) ? child : null, value)] as const
      })
      .filter(([, value]) => !isBlankValue(value)),
  )
}

type Ownership = { kind: 'whole' } | { kind: 'none' } | { kind: 'partial'; slice: SchemaSlice }

const WHOLE: Ownership = { kind: 'whole' }
const NONE: Ownership = { kind: 'none' }

const pathsUnder = (paths: string[], name: string): string[] =>
  paths.filter((path) => path === name || path.startsWith(`${name}.`)).map((path) => path.slice(name.length + 1))

const ownershipOf = (slice: SchemaSlice, name: string): Ownership => {
  if (slice.include) {
    const under = pathsUnder(slice.include, name)
    if (under.length === 0) {
      return NONE
    }
    return under.includes('') ? WHOLE : { kind: 'partial', slice: { include: under } }
  }
  if (slice.exclude) {
    const under = pathsUnder(slice.exclude, name)
    if (under.length === 0) {
      return WHOLE
    }
    return under.includes('') ? NONE : { kind: 'partial', slice: { exclude: under } }
  }
  return WHOLE
}

const slicedGroup = (group: SchemaNode, slice: SchemaSlice): SchemaNode | null => {
  const sliced = sliceSchema(group, slice)
  if (!hasProperties(sliced)) {
    return null
  }
  return presentString(group.title) ? { title: group.title, ...sliced } : sliced
}

export const sliceSchema = (schema: SchemaNode, slice: SchemaSlice): SchemaNode => {
  if (!slice.include && !slice.exclude) {
    return schema
  }
  const kept: SchemaNode = {}
  Object.entries(schemaProperties(schema)).forEach(([name, child]) => {
    const owned = ownershipOf(slice, name)
    if (owned.kind === 'none') {
      return
    }
    if (owned.kind === 'whole') {
      kept[name] = child
      return
    }
    const group = isRecord(child) ? slicedGroup(child, owned.slice) : null
    if (group) {
      kept[name] = group
    }
  })
  const required = (Array.isArray(schema.required) ? (schema.required as string[]) : []).filter((name) => name in kept)

  if (slice.include) {
    return Object.keys(kept).length === 0 ? {} : { type: 'object', properties: kept, required }
  }
  return { ...schema, properties: kept, required }
}

export const applyPanelValues = (values: Values, formData: Values | undefined, schema: SchemaNode, slice: SchemaSlice): Values => {
  const properties = schemaProperties(schema)
  const names = [...new Set([...Object.keys(values), ...Object.keys(formData ?? {})])]
  const next: Values = {}
  names.forEach((name) => {
    const owned = name in properties ? ownershipOf(slice, name) : NONE
    const entered = values[name]
    const answered = formData?.[name]
    switch (owned.kind) {
      case 'none':
        if (name in values) {
          next[name] = entered
        }
        return
      case 'whole':
        if (formData && name in formData) {
          next[name] = answered
        }
        return
      case 'partial': {
        const child = properties[name]
        next[name] = applyPanelValues(isRecord(entered) ? entered : {}, isRecord(answered) ? answered : undefined, isRecord(child) ? child : {}, owned.slice)
      }
    }
  })
  return next
}
